import { useState } from 'preact/hooks';
import { StarRating } from '../../components/StarRating.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { lookupFailedMessage } from '../../components/ui/resultWording.js';
import { useFetch } from '../../hooks/useFetch.js';
import { mytDate } from '../../utils/mytTime.js';

/** 363 (design 2e): how many more reviews "Show more" adds. */
const STEP = 10;
/** `GET /reviews` caps a page at 100 (`parsePageParams`); the list grows by its limit up to there. */
const MAX = 100;

interface CarReview {
  id: string;
  rating: number;
  comment?: string;
  created_at: string;
  author: { name: string } | null;
}

interface CarSummary {
  label?: string;
  name?: string;
  rating?: { average: number; count: number } | null;
}

interface CarReviewsPageProps {
  id?: string;
  path?: string;
}

/**
 * 363 (#1090, FR-013, design 2e): every review of a car, newest first, 10 at a time. Reached from "See all N
 * reviews" on the car page, which used to say "Showing 5 of 23" and lead nowhere. The list grows by its limit
 * rather than by page, so "Show more" keeps what is already on screen.
 */
export function CarReviewsPage({ id }: CarReviewsPageProps) {
  const [limit, setLimit] = useState(STEP);
  const { data: car } = useFetch<CarSummary>(id ? `/cars/${id}` : null);
  const reviewsRead = useFetch<{ items: CarReview[]; total: number }>(
    id ? `/reviews?car_id=${id}&limit=${limit}` : null,
  );
  const items = reviewsRead.data?.items ?? [];
  const total = reviewsRead.data?.total ?? 0;
  const failed =
    reviewsRead.status !== 'ok' &&
    reviewsRead.status !== 'loading' &&
    reviewsRead.status !== 'idle';

  return (
    <div className="bg-bg px-4 py-6">
      <PageHeader
        title={`Reviews · ${car?.label ?? car?.name ?? 'Car'}`}
        fallback={`/cars/${id ?? ''}`}
      />

      {car?.rating && (
        <div data-testid="car-reviews-summary" className="mt-2 mb-4 flex items-center gap-3">
          <span className="text-2xl font-bold text-text">{car.rating.average.toFixed(1)}</span>
          <StarRating value={car.rating.average} size={16} />
          <span className="text-sm text-gray-400">
            {car.rating.count} {car.rating.count === 1 ? 'review' : 'reviews'}
          </span>
        </div>
      )}

      {failed && (
        <p data-testid="car-reviews-error" className="text-sm text-error">
          {lookupFailedMessage('Reviews')}
        </p>
      )}

      <div className="flex flex-col gap-3">
        {items.map((review) => (
          <div
            key={review.id}
            data-testid="car-review"
            className="bg-surface-card rounded-xl p-4 flex flex-col gap-2 shadow-lg"
          >
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-text">
                {review.author?.name ?? 'Anonymous'}
              </span>
              <span className="text-xs text-gray-400">{mytDate(review.created_at)}</span>
            </div>
            <StarRating value={review.rating} size={14} />
            {review.comment && <p className="text-sm text-text">{review.comment}</p>}
          </div>
        ))}
      </div>

      {total > items.length && limit < MAX && (
        <button
          type="button"
          onClick={() => setLimit((n) => Math.min(n + STEP, MAX))}
          className="mt-4 h-11 w-full rounded-full border border-border text-sm font-semibold text-text"
        >
          Show more
        </button>
      )}
    </div>
  );
}
